import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';

import { isLocalURL } from '$utilities/browser';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 * @typedef {Object} ExtendedProps
 * @prop {('a'|'button'|'div')} [as=button] DOM element
 *
 * 1. Generates default attributes based on the button type and link location.
 */
const Button = forwardRef((
    /**
     * @type {Omit<Props, 'as' | 'look' | 'size'> & ExtendedProps}
     */
    {
        as: ComponentTag = 'button',
        children,
        className = '',
        disabled,
        renderIcon,
        ...remainingProps
    },
    /**@type {React.ForwardedRef<(HTMLButtonElement | HTMLAnchorElement | HTMLDivElement)>} */
    ref
) => {
    const buttonClasses = [];
    const defaultAttributes = {};

    /* [1] */
    switch (ComponentTag) {
    case 'a':
        if (remainingProps.href) {
            if (!isLocalURL(remainingProps.href)) {
                defaultAttributes.rel = 'noopener noreferrer';
                defaultAttributes.target = '_blank';
            }
        } else {
            if(!remainingProps.href) {
                console.warn('Missing href attribute on <Button> component');
            }
        }
        break;

    case 'button':
        defaultAttributes.type = 'button';
        break;
    }

    buttonClasses.push(className);

    if (disabled) {
        buttonClasses.push('muted');
    } else {
        buttonClasses.push('cursor-pointer');
    }

    return (
        <ComponentTag
            className={buttonClasses.join(' ')}
            disabled={disabled}
            {...defaultAttributes}
            {...remainingProps}
            ref={ref}>
            {children}

            {renderIcon && (
                <span
                    aria-hidden={true}
                    className={`${children ? ' ml-5' : ''}`}>
                    {renderIcon}
                </span>
            )}
        </ComponentTag>
    );
});

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    active: PropTypes.bool,
    as: PropTypes.oneOf(['a', 'button', 'div']),
    children: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.element,
        PropTypes.string
    ]),
    className: PropTypes.string,
    disabled: PropTypes.bool,
    renderIcon: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.element,
        PropTypes.string
    ]),
};

Button.propTypes = propTypes;
Button.displayName = 'Button';

export default Button;
